'use client'

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { usePathname } from '@/i18n/navigation'
import WorkspacePageClient from '@/app/[locale]/workspace/WorkspacePageClient'
import WorkspaceAiChatPageLoader from '@/app/[locale]/workspace/ai-chat/WorkspaceAiChatPageLoader'
import WorkspaceAiSquarePageClient from '@/app/[locale]/workspace/ai-square/WorkspaceAiSquarePageClient'

type WorkspaceKeepAlivePanel = 'workspace' | 'ai-chat' | 'ai-square'

const WORKSPACE_KEEP_ALIVE_PANELS: WorkspaceKeepAlivePanel[] = ['workspace', 'ai-chat', 'ai-square']

function getWorkspaceKeepAlivePanel(pathname: string | null): WorkspaceKeepAlivePanel | null {
  if (pathname === '/workspace') return 'workspace'
  if (pathname === '/workspace/ai-chat') return 'ai-chat'
  if (pathname === '/workspace/ai-square') return 'ai-square'
  return null
}

function WorkspaceKeepAlivePanelFrame({
  active,
  children,
}: {
  active: boolean
  children: ReactNode
}) {
  return (
    <div
      hidden={!active}
      aria-hidden={!active}
      data-workspace-keep-alive-panel={active ? 'active' : 'hidden'}
    >
      {children}
    </div>
  )
}

export default function WorkspaceKeepAliveShell({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const activePanel = getWorkspaceKeepAlivePanel(pathname)
  const [visitedPanels, setVisitedPanels] = useState<WorkspaceKeepAlivePanel[]>([])

  useEffect(() => {
    if (!activePanel) return
    setVisitedPanels((current) => (current.includes(activePanel) ? current : [...current, activePanel]))
  }, [activePanel])

  const mountedPanels = useMemo(() => {
    if (!activePanel) return []
    return WORKSPACE_KEEP_ALIVE_PANELS.filter((panel) => panel === activePanel || visitedPanels.includes(panel))
  }, [activePanel, visitedPanels])

  if (!activePanel) return <>{children}</>

  return (
    <>
      {mountedPanels.includes('workspace') ? (
        <WorkspaceKeepAlivePanelFrame active={activePanel === 'workspace'}>
          <WorkspacePageClient />
        </WorkspaceKeepAlivePanelFrame>
      ) : null}

      {mountedPanels.includes('ai-chat') ? (
        <WorkspaceKeepAlivePanelFrame active={activePanel === 'ai-chat'}>
          <WorkspaceAiChatPageLoader />
        </WorkspaceKeepAlivePanelFrame>
      ) : null}

      {mountedPanels.includes('ai-square') ? (
        <WorkspaceKeepAlivePanelFrame active={activePanel === 'ai-square'}>
          <WorkspaceAiSquarePageClient />
        </WorkspaceKeepAlivePanelFrame>
      ) : null}
    </>
  )
}
